
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import moment from 'moment';

  export default defineComponent({
    name: 'news-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const submitPullButton = ref<HTMLButtonElement | null>(null);
      const fileInput = ref<HTMLInputElement | null>(null);
      const { can } = useAbility();
      const uploadedFile = ref<File | null>(null);
      const previewImage = ref<string>('');

      const news = ref({
        image: '',
        titleEn: '',
        titleAr: '',
        descriptionAr: '',
        descriptionEn: '',
        date: new Date(),
        readTime: undefined,
      });

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const handleImageUrlChange = () => {
        uploadedFile.value = null;
        previewImage.value = '';
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      };

      const handleFileUpload = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file) {
          uploadedFile.value = file;
          // Create preview URL
          const reader = new FileReader();
          reader.onload = (e) => {
            previewImage.value = e.target?.result as string;
          };
          reader.readAsDataURL(file);
        }
      };

      const newsSchema = Yup.object().shape({
        titleAr: Yup.string()
          .required(() => translate('AR_TITLE_IS_REQUIRED_FIELD'))
          .label('Arabic Title'),
        titleEn: Yup.string()
          .required(() => translate('EN_TITLE_IS_REQUIRED_FIELD'))
          .label('Arabic Title'),
        externalLink: Yup.string()
          .required(() => translate('EXTERNAL_LINK_IS_REQUIRED_FIELD'))
          .label('ExternalLink'),
      });

      const urlSchema = Yup.object().shape({
        url: Yup.string()
          .required(() => translate('URL_IS_REQUIRED_FIELD'))
          .label('Url'),
      });

      const onSubmitPull = async (values) => {
        if (submitPullButton.value) {
          // eslint-disable-next-line
          submitPullButton.value!.disabled = true;
          submitPullButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const data = await store.dispatch(Actions.PULL_URL_NEWS, values);
        data.date = moment(data.date).format('YYYY-MM-DD');
        news.value = data;

        uploadedFile.value = null;
        previewImage.value = '';
        if (fileInput.value) {
          fileInput.value.value = '';
        }

        if (!news.value) {
          return;
        }
        if (data.title) {
          news.value.titleEn = data.title;
          news.value.titleAr = data.title;
        }

        if (data.description) {
          news.value.descriptionEn = data.description;
          news.value.descriptionAr = data.description;
        }

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitPullButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitPullButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_PULLED_DATA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitPullButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
            submitPullButton.value!.disabled = false;
          });
        }
      };

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        let logoFormData;
        if (uploadedFile.value) {
          logoFormData = new FormData();
          logoFormData.append('images', uploadedFile.value);
        }

        const response = await store.dispatch(Actions.CREATE_NEWS, values);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          if (logoFormData && uploadedFile.value) {
            const createdNewsId = response?.data?.id || response?.id;
            if (createdNewsId) {
              await store.dispatch(Actions.UPLOAD_NEWS_THUMBNAIL, {
                data: logoFormData,
                id: createdNewsId,
              });
            }
          }

          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_NEWS'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'news-listing' });
          });
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('newsCreating'), [
          translate('assets'),
        ]);
      });

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitCreate,
        onSubmitPull,
        handleFileUpload,
        handleImageUrlChange,
        submitButton,
        submitPullButton,
        previewImage,
        fileInput,
        translate,
        news,
        newsSchema,
        urlSchema,
        goBack,
        can,
      };
    },
  });
